/**
 * The template module (ADR 0024). Two kinds, one list:
 * - general templates: pages in the hidden "Sjablonen" folder, with their
 *   settings in `nexus.templateConfig` (target, title pattern, questions, daily);
 * - database templates: hidden rows of a database (`nexus.template`), as in 5b.
 * Using one makes a page (or a database row) and fills the placeholders in its
 * title, properties and text.
 */
import type { JSONContent } from '@tiptap/core';
import {
  EMPTY_TEMPLATE_CONFIG,
  TEMPLATE_CONFIG_PROP,
  TEMPLATE_PROP,
  fillTemplateText,
  fillTemplateValue,
  formatDate,
  latestOccurrence,
  readTemplateConfig,
  type TemplateConfig,
  type TemplateVars,
} from '@nexus/core';
import { ensureTaskIds } from '@nexus/editor';
import type { PageRecord, Store } from '@nexus/store';
import { isEmptyDoc, loadPageJSON, reindexPage } from './content';
import { addRow, getSchema, withoutTaskIds } from './database';
import { mutatePageDoc } from './docs';
import { ensureSpecialPage } from './tasks';

export const TEMPLATES_FOLDER = 'Sjablonen';

export const templatesFolder = (store: Store) =>
  ensureSpecialPage(store, 'templates_parent_id', TEMPLATES_FOLDER, '🧩');

export interface TemplateInfo {
  page: PageRecord;
  config: TemplateConfig;
  /** For a database template: its database. */
  database: PageRecord | null;
}

/** All templates: general ones first (by title), then those of each database. */
export async function listAllTemplates(store: Store): Promise<TemplateInfo[]> {
  const folderId = await store.getSetting('templates_parent_id');
  const pages = await store.listPagesWithProps();
  const byId = new Map(pages.map((p) => [p.id, p]));
  const general: TemplateInfo[] = [];
  const perDatabase: TemplateInfo[] = [];
  for (const p of pages) {
    if (folderId && p.parentId === folderId)
      general.push({
        page: p,
        config: readTemplateConfig(p.props[TEMPLATE_CONFIG_PROP]),
        database: null,
      });
    else if (p.props[TEMPLATE_PROP] === true && p.parentId && byId.has(p.parentId))
      perDatabase.push({
        page: p,
        config: { ...EMPTY_TEMPLATE_CONFIG, targetId: p.parentId },
        database: byId.get(p.parentId)!,
      });
  }
  const byTitle = (a: TemplateInfo, b: TemplateInfo) => a.page.title.localeCompare(b.page.title);
  perDatabase.sort((a, b) => a.database!.title.localeCompare(b.database!.title) || byTitle(a, b));
  return [...general.sort(byTitle), ...perDatabase];
}

export async function getTemplate(store: Store, id: string): Promise<TemplateInfo | null> {
  return (await listAllTemplates(store)).find((t) => t.page.id === id) ?? null;
}

/** A new, empty general template, ready to edit. */
export async function createGeneralTemplate(
  store: Store,
  title: string,
  config: Partial<TemplateConfig> = {},
): Promise<PageRecord> {
  const folder = await templatesFolder(store);
  const page = await store.createPage({ title, parentId: folder.id });
  await store.setProp(page.id, TEMPLATE_CONFIG_PROP, { ...EMPTY_TEMPLATE_CONFIG, ...config });
  return page;
}

export async function setTemplateConfig(store: Store, id: string, config: TemplateConfig) {
  await store.setProp(id, TEMPLATE_CONFIG_PROP, config);
}

/** The template used for new daily notes: marked "daily", or named "Dagnotitie". */
export async function dailyTemplate(store: Store): Promise<TemplateInfo | null> {
  const general = (await listAllTemplates(store)).filter((t) => !t.database);
  return (
    general.find((t) => t.config.daily) ??
    general.find((t) =>
      /^((template|sjabloon)\s*[-–:]\s*)?dagnotitie$/i.test(t.page.title.trim()),
    ) ??
    null
  );
}

// ---- filling ---------------------------------------------------------------------------

export interface UseTemplateInput {
  /** The title asked for (also {{title}}); ignored for the page title when there is a pattern. */
  title: string;
  /** Answers to the questions, by label. Page questions hold a page title. */
  fields?: Record<string, string>;
  locale: string;
  now?: Date;
  /** Use `title` as is, never the title pattern (daily notes are named by date). */
  exactTitle?: boolean;
  /** Overrides the template's target (e.g. the "New" button of a database). */
  targetId?: string | null;
}

function fillJSON(node: JSONContent, vars: TemplateVars): JSONContent {
  const attrs =
    node.attrs && typeof node.attrs.title === 'string'
      ? { ...node.attrs, title: fillTemplateText(node.attrs.title, vars) }
      : node.attrs;
  return {
    ...node,
    ...(node.text !== undefined ? { text: fillTemplateText(node.text, vars) } : {}),
    ...(attrs ? { attrs } : {}),
    ...(node.content ? { content: node.content.map((n) => fillJSON(n, vars)) } : {}),
  };
}

/** The template's text, filled, with fresh task ids: for inserting at the cursor. */
export async function templateContent(
  store: Store,
  templateId: string,
  input: UseTemplateInput,
): Promise<JSONContent> {
  const info = await getTemplate(store, templateId);
  const vars = varsFor(info, input, false);
  const doc = await loadPageJSON(store, templateId);
  return ensureTaskIds(fillJSON(withoutTaskIds(doc), vars)).doc;
}

function varsFor(
  info: TemplateInfo | null,
  input: UseTemplateInput,
  asLinks: boolean,
): TemplateVars {
  const pageFields = new Set(
    (info?.config.fields ?? []).filter((f) => f.type === 'page').map((f) => f.label.toLowerCase()),
  );
  const fields = Object.fromEntries(
    Object.entries(input.fields ?? {}).map(([k, v]) => [
      k,
      asLinks && v && pageFields.has(k.toLowerCase()) ? `[[${v}]]` : v,
    ]),
  );
  return { title: input.title, now: input.now ?? new Date(), locale: input.locale, fields };
}

/** Make a page (or database row) from a template. */
export async function applyTemplate(
  store: Store,
  templateId: string,
  input: UseTemplateInput,
): Promise<PageRecord> {
  const info = await getTemplate(store, templateId);
  if (!info) throw new Error('Template not found');
  const vars = varsFor(info, input, false);
  // Properties hold page answers as links, like other relation values.
  const propVars = varsFor(info, input, true);
  const title = (
    info.config.titlePattern && !input.exactTitle
      ? fillTemplateText(info.config.titlePattern, vars)
      : input.title
  ).trim();

  let targetId = input.targetId !== undefined ? input.targetId : info.config.targetId;
  const target = targetId ? await store.getPage(targetId) : null;
  if (!target || target.deleted || target.trashedAt) targetId = null;
  const schema = targetId ? await getSchema(store, targetId) : null;

  let page: PageRecord;
  if (targetId && schema) {
    page = await addRow(store, targetId, schema, { title, templateId });
  } else {
    page = await store.createPage({ title, parentId: targetId, icon: info.page.icon });
    for (const [key, value] of Object.entries(await store.getProps(templateId)))
      if (!key.startsWith('nexus.')) await store.setProp(page.id, key, value);
    const doc = await loadPageJSON(store, templateId);
    if (!isEmptyDoc(doc))
      await mutatePageDoc(store, page.id, () => ensureTaskIds(withoutTaskIds(doc)).doc);
  }

  // Fill the placeholders in the properties and the text.
  for (const [key, value] of Object.entries(await store.getProps(page.id))) {
    const filled = fillTemplateValue(value, propVars);
    if (JSON.stringify(filled) !== JSON.stringify(value)) await store.setProp(page.id, key, filled);
  }
  const filled = await mutatePageDoc(store, page.id, (doc) => {
    const next = fillJSON(doc, vars);
    return JSON.stringify(next) === JSON.stringify(doc) ? null : next;
  });
  await reindexPage(store, page.id, filled ?? (await loadPageJSON(store, page.id)));
  return page;
}

// ---- Obsidian templates ----------------------------------------------------------------

export const TEMPLATES_MIGRATION_KEY = 'templates_v1';

/**
 * One-off: make the imported Obsidian templates ready for the module. Drops the
 * "Template - " prefix, marks the daily-note template and points Vergadering,
 * Project and Taak at their databases. A property that only holds {{title}}
 * (Obsidian's `title:`) is dropped: the page title is the title.
 */
export async function upgradeObsidianTemplates(store: Store): Promise<number> {
  if (await store.getSetting(TEMPLATES_MIGRATION_KEY)) return 0;
  const targets: [RegExp, string][] = [
    [/^vergadering/i, 'meetings_db_id'],
    [/^project/i, 'projects_db_id'],
    [/^taak/i, 'tasks_parent_id'],
  ];
  let changed = 0;
  for (const t of (await listAllTemplates(store)).filter((x) => !x.database)) {
    const name = t.page.title.replace(/^(template|sjabloon)\s*[-–:]\s*/i, '').trim();
    if (name && name !== t.page.title) await store.updatePage(t.page.id, { title: name });
    const props = await store.getProps(t.page.id);
    if (props[TEMPLATE_CONFIG_PROP] === undefined) {
      const config: TemplateConfig = {
        ...EMPTY_TEMPLATE_CONFIG,
        daily: /^dagnotitie$/i.test(name),
      };
      for (const [re, key] of targets)
        if (re.test(name)) config.targetId = (await store.getSetting(key)) ?? null;
      await setTemplateConfig(store, t.page.id, config);
    }
    for (const [key, value] of Object.entries(props))
      if (
        key.toLowerCase() === 'title' &&
        typeof value === 'string' &&
        /^\{\{\s*title\s*\}\}$/i.test(value)
      )
        await store.setProp(t.page.id, key, undefined);
    changed++;
  }
  await store.setSetting(TEMPLATES_MIGRATION_KEY, String(Date.now()));
  return changed;
}

// ---- recurring templates ---------------------------------------------------------------

/** On a page made by a schedule: "<template id>@<moment>", so it is made only once. */
export const FROM_SCHEDULE_PROP = 'nexus.fromSchedule';

/**
 * Make the pages of recurring templates whose moment has come (the latest moment
 * only). A page already made for that moment — on any device, even if it was
 * trashed since — is never made again.
 */
export async function runRecurringTemplates(
  store: Store,
  opts: { now?: Date; locale: string },
): Promise<PageRecord[]> {
  const now = opts.now ?? new Date();
  const made: PageRecord[] = [];
  for (const t of await listAllTemplates(store)) {
    const schedule = t.database ? null : t.config.schedule;
    if (!schedule) continue;
    const moment = latestOccurrence(schedule, now);
    if (!moment) continue;
    const key = `${t.page.id}@${moment.toISOString()}`;
    if ((await store.findPropsWithValue(key)).length > 0) continue;
    const dated = formatDate(moment, 'YYYY-MM-DD', opts.locale);
    const page = await applyTemplate(store, t.page.id, {
      title: `${t.page.title} ${dated}`.trim(),
      locale: opts.locale,
      now: moment,
    });
    await store.setProp(page.id, FROM_SCHEDULE_PROP, key);
    made.push(page);
  }
  return made;
}
